type Entry = { value: unknown; expires: number };
const entries = new Map<string, Entry>();
const pending = new Map<string, Promise<unknown>>();
let revision = 0;
const clone = <T>(value: T): T => structuredClone(value);

export const requestCache = {
  get revision() {
    return revision;
  },
  clear() {
    revision++;
    entries.clear();
    pending.clear();
  },
  seed<T>(key: string, value: T, expectedRevision = revision): void {
    if (expectedRevision !== revision) return;
    for (const [existingKey, entry] of entries)
      if (entry.expires <= Date.now()) entries.delete(existingKey);
    if (entries.size >= 100) entries.delete(entries.keys().next().value!);
    entries.set(key, { value: clone(value), expires: Date.now() + 60_000 });
  },
  async load<T>(
    key: string,
    loader: () => Promise<T>,
    signal?: AbortSignal,
  ): Promise<T> {
    signal?.throwIfAborted();
    const entry = entries.get(key);
    if (entry && entry.expires > Date.now()) return clone(entry.value as T);
    entries.delete(key);
    let request = pending.get(key) as Promise<T> | undefined;
    const shared = !!request;
    if (!request) {
      const generation = revision;
      request = loader()
        .then((value) => {
          signal?.throwIfAborted();
          requestCache.seed(key, value, generation);
          return value;
        })
        .finally(() => {
          if (pending.get(key) === request) pending.delete(key);
        });
      pending.set(key, request);
    }
    try {
      const result = await request;
      signal?.throwIfAborted();
      return clone(result);
    } catch (error) {
      // An active consumer can retry when the owner of a shared request unmounts.
      if (
        shared &&
        !signal?.aborted &&
        error instanceof Error &&
        ["CanceledError", "AbortError"].includes(error.name)
      ) {
        return requestCache.load(key, loader, signal);
      }
      throw error;
    }
  },
};
export const contratsCacheKey = (exploitation: number, millesime: number) =>
  `contrats:${exploitation}:${millesime}`;
